import * as THREE from "three";

export type Coords = {
  a: THREE.Vector3,
  b: THREE.Vector3
};

type InitializedLine3D = Line3D & {
  scene: THREE.Scene;
  material: THREE.LineBasicMaterial;
  maxDistance: number;
  coords: Coords;
  id: string;
  line: THREE.Line;
  vectorDirector: THREE.Vector3;
};

export default class Line3D {
  scene: THREE.Scene;
  material = new THREE.LineBasicMaterial({ color: 0x0000ff });
  maxDistance: number;
  coords: Coords;
  id: string;
  line: THREE.Line;

  vectorDirector: THREE.Vector3;

  constructor(coords: Coords, scene: THREE.Scene, maxDistance: number, id: string) {
    this.scene = scene;
    this.maxDistance = maxDistance;
    this.coords = {
      a: coords.a.clone(),
      b: coords.b.clone()
    };
    this.id = id;

    this.vectorDirector = this.coords.b.clone().sub(this.coords.a);
    const points = [
      this.linePoint(this.maxDistance),
      this.linePoint(this.maxDistance * -1)
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    this.line = new THREE.Line(geometry, this.material);
  }

  isInitialized(): this is InitializedLine3D {
    const missingProperties = [
      ["scene", this.scene],
      ["material", this.material],
      ["maxDistance", this.maxDistance],
      ["coords", this.coords],
      ["id", this.id],
      ["line", this.line],
      ["vectorDirector", this.vectorDirector]
    ].filter(([, value]) => value === null || value === undefined).map(([property]) => property);

    if (missingProperties.length > 0) {
      console.warn(`Line3D - missing initialized properties: ${missingProperties.join(", ")}`);
      return false;
    }

    return true;
  }

  private linePoint(t: number) {
    return new THREE.Vector3(
      this.coords.a.x - (t * this.vectorDirector.x),
      this.coords.a.y - (t * this.vectorDirector.y),
      this.coords.a.z - (t * this.vectorDirector.z)
    );
  }

  render() {
    if (!this.isInitialized()) return;

    this.scene.add(this.line);
  }

  dispose(disposeResources = true) {
    if (!this.isInitialized()) return this;

    this.scene.remove(this.line);

    if (disposeResources) {
      this.line.geometry.dispose();

      if (Array.isArray(this.line.material)) {
        this.line.material.forEach(material => material.dispose());
      } else {
        this.line.material.dispose();
      }
    }

    return this;
  }

  intersect(d: Line3D): THREE.Vector3 | false {
    if (!this.isInitialized() || !d.isInitialized()) return false;

    // 1 = this.coords.a
    // 2 = this.coords.b
    // 3 = d.coords.a
    // 4 = d.coords.b
    const v13 = this.coords.a.clone().sub(d.coords.a);
    const v43 = d.coords.b.clone().sub(d.coords.a);
    const v21 = this.coords.b.clone().sub(this.coords.a);

    if (
      (Math.abs(v43.x) < Number.EPSILON && Math.abs(v43.y) < Number.EPSILON && Math.abs(v43.z) < Number.EPSILON) ||
      (Math.abs(v21.x) < Number.EPSILON && Math.abs(v21.y) < Number.EPSILON && Math.abs(v21.z) < Number.EPSILON)
    ) {
      return false;
    }

    const d1343 = (v13.x * v43.x) + (v13.y * v43.y) + (v13.z * v43.z);
    const d4321 = (v43.x * v21.x) + (v43.y * v21.y) + (v43.z * v21.z);
    const d1321 = (v13.x * v21.x) + (v13.y * v21.y) + (v13.z * v21.z);
    const d4343 = (v43.x * v43.x) + (v43.y * v43.y) + (v43.z * v43.z);
    const d2121 = (v21.x * v21.x) + (v21.y * v21.y) + (v21.z * v21.z);

    const denom = d2121 * d4343 - d4321 * d4321;
    if (Math.abs(denom) < Number.EPSILON) {
      return false;
    }
    const numer = d1343 * d4321 - d1321 * d4343;

    const mua = numer / denom;
    const mub = (d1343 + d4321 * mua) / d4343;

    const vA = this.coords.a.clone().add(v21.clone().multiplyScalar(mua));
    const vB = d.coords.a.clone().add(v43.clone().multiplyScalar(mub));

    if (vA.equals(vB)) {
      return vA;
    }

    return false;
  }
}
